import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Instagram, Facebook, Mail, Music, Users, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

const DrUnk = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const bandImage = "/images/dr_unk_logo.jpg";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-amber-950/20">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-lg font-bold truncate">Dr. Unk</h1>
        </div>
      </div>

      {/* Hero Section */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative"
      >
        <div className="aspect-video sm:aspect-[21/9] w-full overflow-hidden">
          <img 
            src={bandImage}
            alt="Dr. Unk - Rock, Blues, Boogie"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
        </div>
        
        {/* Band Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full flex items-center gap-1">
                <Star size={12} /> Sponsored Band
              </span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-bold text-white mb-2">Dr. Unk</h1>
            <p className="text-sm sm:text-base text-gray-300 flex items-center gap-2">
              <Music size={16} /> Rock &bull; Blues &bull; Boogie
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="container mx-auto px-4 py-6 space-y-6">
        
        {/* About Section */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Users size={18} className="text-amber-400" /> About
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Rock, blues, & boogie! Dr. Unk is a Super Group consisting of Minnesota's Top Tier Musicians
                available for booking as a unit or as a backing band for your project or upcoming event!
                Catch us at our next show! Reach out to find out more!
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Book Us Button */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <a 
            href="mailto:mysterymachinetc@gmail.com?subject=Booking%20Inquiry%20-%20Dr.%20Unk&body=Hi%2C%0A%0AI'm%20interested%20in%20booking%20Dr.%20Unk%20for%20an%20upcoming%20event.%0A%0AEvent%20Details%3A%0A-%20Date%3A%0A-%20Venue%3A%0A-%20Type%20of%20Event%3A%0A%0APlease%20let%20me%20know%20your%20availability%20and%20rates.%0A%0AThank%20you!"
            className="block"
          >
            <Button 
              size="lg" 
              className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold py-6 text-lg"
            >
              <Mail className="mr-2" size={20} />
              Book Us!
            </Button>
          </a>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-4">Connect With Us</h2>
              <div className="grid grid-cols-2 gap-3">
                <a 
                  href="https://www.instagram.com/dr.unk.band" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                >
                  <Instagram size={24} className="text-pink-400" />
                  <span className="font-medium">Instagram</span>
                </a>
                <a 
                  href="https://www.facebook.com/share/1V9XmZCqsW/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 p-4 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/20 hover:from-blue-500/30 hover:to-blue-600/30 transition-all"
                >
                  <Facebook size={24} className="text-blue-400" />
                  <span className="font-medium">Facebook</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Instagram Reel */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.52 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Instagram size={18} className="text-pink-400" /> Latest
              </h2>
              <a
                href="https://www.instagram.com/reel/DS5i2V7CZ8g/"
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-xl overflow-hidden border border-border hover:border-pink-500/40 transition-all"
              >
                <div className="relative aspect-[9/16] max-h-[500px] mx-auto bg-black flex items-center justify-center">
                  <iframe
                    src="https://www.instagram.com/reel/DS5i2V7CZ8g/embed/"
                    className="w-full h-full border-0"
                    allowFullScreen
                    loading="lazy"
                    tabIndex={-1}
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    title="Dr. Unk Instagram Reel"
                  />
                </div>
              </a>
            </CardContent>
          </Card>
        </motion.div>

        {/* Upcoming Show Flyer */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.55 }}
        >
          <Card className="bg-card/50 backdrop-blur border-border overflow-hidden">
            <CardContent className="p-0">
              <div className="relative">
                <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-red-600 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-lg shadow-lg uppercase tracking-wider">
                  October 23, 2026
                </div>
                <img
                  src="/images/dr_unk_halloween.jpg"
                  alt="Sinners Juke Joint Halloween Party - Dr. Unk at Gluek's - October 23, 2026"
                  className="w-full h-auto"
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Card */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
            <CardContent className="p-4 sm:p-6 text-center">
              <h3 className="font-semibold mb-2">Ready to bring the rock to your event?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Available for booking for your project or upcoming show
              </p>
              <a href="mailto:mysterymachinetc@gmail.com?subject=Booking%20Inquiry%20-%20Dr.%20Unk">
                <Button variant="outline" className="border-amber-500/50 hover:bg-amber-500/20">
                  <Mail size={16} className="mr-2" />
                  mysterymachinetc@gmail.com
                </Button>
              </a>
            </CardContent>
          </Card>
        </motion.div>

      </div>
    </div>
  );
};

export default DrUnk;
